import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getProveedor, updateProveedor, deleteProveedor, getProveedorPdfUrl, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Loader2, Mail, Phone, MapPin, Clock, Truck, FileText, ExternalLink, Trash2, Archive, ArchiveRestore, LogOut, MoreVertical } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { Textarea } from "@/components/ui/textarea";

export default function ProveedorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [proveedor, setProveedor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notaEdit, setNotaEdit] = useState("");
  const [savingNota, setSavingNota] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.proveedores === false) {
      if (user.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user.permissions?.formularios !== false) navigate("/formularios", { replace: true });
      else if (user.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
      return;
    }
    loadProveedor();
  }, [id, user, navigate]);

  const loadProveedor = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await getProveedor(id);
      setProveedor(data);
      setNotaEdit(data.nota ?? "");
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar el proveedor");
      navigate("/proveedores");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNota = async () => {
    if (proveedor?.nota === notaEdit) return;
    setSavingNota(true);
    try {
      await updateProveedor(id, { nota: notaEdit || null });
      setProveedor((p) => (p ? { ...p, nota: notaEdit || null } : p));
      toast.success("Nota guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSavingNota(false);
    }
  };

  const handleToggleArchivado = async () => {
    try {
      await updateProveedor(id, { archivado: !proveedor.archivado });
      setProveedor((p) => (p ? { ...p, archivado: !p.archivado } : p));
      toast.success(proveedor.archivado ? "Proveedor restaurado" : "Proveedor archivado");
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al actualizar");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProveedor(id);
      toast.success("Proveedor eliminado");
      navigate("/proveedores");
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al eliminar");
    } finally {
      setIsDeleting(false);
    }
  };

  const openPdf = () => {
    const url = getProveedorPdfUrl(id);
    const token = getToken();
    window.open(`${url}?token=${token}`, "_blank");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!proveedor) {
    return null;
  }

  const nombre = proveedor.nombre || proveedor.razon_social || "Sin nombre";

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/proveedores")} className="text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Proveedores
          </Button>
          <nav className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate("/candidatos")} className="text-sm">
              Candidatos
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/formularios")} className="text-sm">
              Formularios
            </Button>
            {user?.permissions?.proveedores !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/proveedores")} className="text-sm bg-muted">
                Proveedores
              </Button>
            )}
            {user?.permissions?.archivos !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/brutalfiles")} className="text-sm">
                Archivos
              </Button>
            )}
            {user?.permissions?.admin !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-sm">
                Admin
              </Button>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 text-sm text-muted-foreground px-2 md:px-3">
                  <UserAvatar user={user} size={28} />
                  <span className="hidden md:inline max-w-[120px] truncate">{user?.nombre || user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight mb-1">{nombre}</h1>
            {proveedor.razon_social && proveedor.razon_social !== nombre && (
              <p className="text-muted-foreground">{proveedor.razon_social}</p>
            )}
            {proveedor.nif && (
              <p className="text-sm text-muted-foreground mt-1">NIF/CIF: {proveedor.nif}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {proveedor.url && (
              <Button variant="outline" size="sm" asChild>
                <a href={proveedor.url} target="_blank" rel="noopener noreferrer" className="gap-2">
                  <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  Ver web
                </a>
              </Button>
            )}
            {proveedor.tiene_pdf && (
              <Button variant="outline" size="sm" onClick={openPdf} className="gap-2">
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                Ver PDF
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <MoreVertical className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleToggleArchivado}>
                  {proveedor.archivado ? (
                    <><ArchiveRestore className="w-4 h-4 mr-2" /> Restaurar</>
                  ) : (
                    <><Archive className="w-4 h-4 mr-2" /> Archivar</>
                  )}
                </DropdownMenuItem>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive focus:text-destructive">
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Se eliminará "{nombre}" de forma permanente. Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {proveedor.archivado && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-4 py-2 mb-6 text-sm">
            Este proveedor está archivado.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {proveedor.resumen_ia && (
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-2">Resumen</h2>
                <p className="text-sm">{proveedor.resumen_ia}</p>
              </section>
            )}

            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Contacto</h2>
              <div className="space-y-2">
                {proveedor.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                    <a href={`mailto:${proveedor.email}`} className="text-primary hover:underline truncate">{proveedor.email}</a>
                  </div>
                )}
                {proveedor.telefono && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                    <a href={`tel:${proveedor.telefono}`} className="text-primary hover:underline">{proveedor.telefono}</a>
                  </div>
                )}
                {!proveedor.email && !proveedor.telefono && (
                  <p className="text-sm text-muted-foreground">Sin datos de contacto</p>
                )}
              </div>
            </section>

            {(proveedor.direccion || proveedor.ciudad) && (
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Dirección</h2>
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    {proveedor.direccion && <p>{proveedor.direccion}</p>}
                    {(proveedor.codigo_postal || proveedor.ciudad || proveedor.provincia) && (
                      <p className="text-muted-foreground">
                        {[proveedor.codigo_postal, proveedor.ciudad, proveedor.provincia].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {(proveedor.horario || proveedor.tiempos_envio) && (
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Horario y envíos</h2>
                <div className="space-y-2 text-sm">
                  {proveedor.horario && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                      {proveedor.horario}
                    </div>
                  )}
                  {proveedor.tiempos_envio && (
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                      {proveedor.tiempos_envio}
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          <div>
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-2">Nota</h2>
              <Textarea
                value={notaEdit}
                onChange={(e) => setNotaEdit(e.target.value)}
                onBlur={handleSaveNota}
                placeholder="Añade una nota interna..."
                className="min-h-[120px] resize-y"
              />
              {savingNota && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> Guardando...
                </p>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
